import React, { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Container,
  Grid,
  Paper,
  Typography
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Business as BusinessIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  Email as EmailIcon,
  LocationOn as LocationIcon,
  Phone as PhoneIcon
} from '@mui/icons-material';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { deleteSupplier, getSupplierById } from '../features/suppliers/services/supplierService';
import ConfirmationDialog from '../components/ConfirmationDialog';

const SupplierDetailPage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { id } = useParams();
  const [supplier, setSupplier] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);

  useEffect(() => {
    const fetchSupplier = async () => {
      try {
        setLoading(true);
        setError('');
        const response = await getSupplierById(id);
        setSupplier(response?.payload || response?.data || response);
      } catch (fetchError) {
        setError(fetchError.response?.data?.message || t('supplier_not_found') || 'Supplier could not be loaded.');
      } finally {
        setLoading(false);
      }
    };

    fetchSupplier();
  }, [id, t]);

  const displayValue = (value) => value || '-';
  const address = [supplier?.Street, supplier?.StreetNumber, supplier?.PostCode, supplier?.City, supplier?.Country]
    .filter(Boolean)
    .join(', ');
  const isActive = supplier?.IsActive !== false;

  const handleDelete = async () => {
    setDeleteLoading(true);
    try {
      await deleteSupplier(id, { suppressErrorToast: true });
      navigate('/suppliers');
    } catch (deleteError) {
      setError(deleteError.response?.data?.message || t('supplier_delete_error') || 'Failed to delete supplier.');
      setDeleteLoading(false);
      setDeleteDialogOpen(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 10 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error || !supplier) {
    return (
      <Container maxWidth="md" sx={{ mt: 4 }}>
        <Alert severity="error" sx={{ mb: 2 }}>{error || t('supplier_not_found') || 'Supplier not found.'}</Alert>
        <Button startIcon={<ArrowBackIcon />} onClick={() => navigate('/suppliers')}>
          {t('back_to_suppliers') || 'Back to suppliers'}
        </Button>
      </Container>
    );
  }

  const DetailItem = ({ label, value, icon }) => (
    <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'flex-start' }}>
      {icon && <Box sx={{ color: 'text.secondary', mt: 0.25 }}>{icon}</Box>}
      <Box>
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', textTransform: 'uppercase' }}>
          {label}
        </Typography>
        <Typography variant="body1">{displayValue(value)}</Typography>
      </Box>
    </Box>
  );

  return (
    <Container maxWidth="lg" sx={{ mt: 2, mb: 4 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 3, gap: 2 }}>
        <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center' }}>
          <BusinessIcon color="primary" sx={{ fontSize: 40 }} />
          <Box>
            <Typography variant="h4" component="h1" sx={{ fontWeight: 600, color: 'primary.main' }}>
              {displayValue(supplier.Name || supplier.SupplierName)}
            </Typography>
            <Typography variant="body2" color="text.secondary">{t('supplier_details') || 'Supplier details'}</Typography>
          </Box>
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button variant="outlined" startIcon={<ArrowBackIcon />} onClick={() => navigate('/suppliers')}>
            {t('back') || 'Back'}
          </Button>
          <Button variant="contained" startIcon={<EditIcon />} onClick={() => navigate(`/suppliers/edit/${id}`)}>
            {t('edit_supplier') || 'Edit'}
          </Button>
          <Button variant="outlined" color="error" startIcon={<DeleteIcon />} onClick={() => setDeleteDialogOpen(true)}>
            {t('delete_supplier') || 'Delete'}
          </Button>
        </Box>
      </Box>

      <Paper elevation={2} sx={{ p: { xs: 2, md: 4 }, borderRadius: 2 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Typography variant="h6" fontWeight={600}>{t('basic_information') || 'Basic information'}</Typography>
          <Chip label={isActive ? (t('active') || 'Active') : (t('inactive') || 'Inactive')} color={isActive ? 'success' : 'default'} variant="outlined" />
        </Box>

        <Grid container spacing={3}>
          <Grid item xs={12} md={6}><DetailItem label={t('contact_person') || 'Contact person'} value={supplier.Contactperson || supplier.ContactPerson} icon={<BusinessIcon fontSize="small" />} /></Grid>
          <Grid item xs={12} md={6}><DetailItem label={t('email_address') || 'Email'} value={supplier.Email} icon={<EmailIcon fontSize="small" />} /></Grid>
          <Grid item xs={12} md={6}><DetailItem label={t('phone_number') || 'Phone'} value={supplier.MobileNumber || supplier.Phone} icon={<PhoneIcon fontSize="small" />} /></Grid>
          <Grid item xs={12} md={6}><DetailItem label={t('tax_number') || 'Tax number'} value={supplier.BTWNumber || supplier.TaxNumber} /></Grid>
          <Grid item xs={12}><DetailItem label={t('address') || 'Address'} value={address} icon={<LocationIcon fontSize="small" />} /></Grid>
          <Grid item xs={12}><DetailItem label={t('notes') || 'Notes'} value={supplier.Notes} /></Grid>
        </Grid>
      </Paper>
      <ConfirmationDialog
        open={deleteDialogOpen}
        title={t('delete_supplier') || 'Delete supplier'}
        message={t('confirm_delete_supplier', { supplier: supplier.Name || supplier.SupplierName }) || `Are you sure you want to delete "${supplier.Name || supplier.SupplierName}"?`}
        confirmText={t('delete_supplier') || 'Delete'}
        cancelText={t('cancel') || 'Cancel'}
        variant="danger"
        loading={deleteLoading}
        onCancel={() => !deleteLoading && setDeleteDialogOpen(false)}
        onConfirm={handleDelete}
      />
    </Container>
  );
};

export default SupplierDetailPage;